import React, { useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';
import { updateOpenGraphMetaTags, resetOpenGraphMetaTags } from '../../utils/metaTags';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px); /* Account for progress bar */
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 100vh;
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra bottom padding to ensure CTA is fully visible and scrollable */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  width: 100%;
  /* Minimal padding to ensure content extends beyond viewport */
  padding-bottom: ${theme.spacing.md};
`;

const TextContent = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.body};
  }
`;

const ImageContainer = styled.div`
  margin: ${theme.spacing.lg} 0;
  text-align: center;
`;

const Image = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px; /* Ensure good touch target size */
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  /* Position relative to ensure proper stacking context */
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
    margin-bottom: ${theme.spacing.lg}; /* Small margin to ensure button is accessible */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem 2.0rem 1rem;
    min-width: 150px;
  }
`;

const LearningScreen = ({ data, onNext, userProgress = {} }) => {
  // Update Open Graph meta tags when component mounts or data changes
  useEffect(() => {
    const ogTags = data.content?.ogMetaTags;
    if (ogTags) {
      // Check if at least one tag has a non-empty value
      const hasValues = Object.values(ogTags).some(value => {
        if (value === null || value === undefined) return false;
        const stringValue = String(value).trim();
        return stringValue !== '';
      });
      
      if (hasValues) {
        // Only update if at least one tag has a value
        updateOpenGraphMetaTags(ogTags);
      }
    }

    // Cleanup: reset to defaults when component unmounts
    return () => {
      resetOpenGraphMetaTags();
    };
  }, [data.content?.ogMetaTags]);

  const handleContinue = () => {
    onNext?.(data.key);
  };

  // Get formatted HTML content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  return (
    <Container>
      <Content>
        {htmlContent && (
          <TextContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {data.content?.images && data.content.images.map((imageUrl, index) => (
          <ImageContainer key={index}>
            <Image src={imageUrl} alt={`Content image ${index + 1}`} />
          </ImageContainer>
        ))}
        
        {/* CTA button appears right after content */}
        <ContinueButton onClick={handleContinue}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
        
        {/* Spacer to ensure content extends beyond viewport and prevents bounce-back */}
        <div style={{ height: '1px', width: '100%' }} />
      </Content>
    </Container>
  );
};

export default LearningScreen; 